// Global request manager to prevent resource exhaustion
class RequestManager {
  constructor() {
    this.activeRequests = new Map();
    this.requestQueue = [];
    this.circuitBreakers = new Map();
    this.globalThrottle = {
      lastRequest: 0,
      minInterval: 30000, // 30 seconds minimum between any requests
      maxConcurrent: 1 // Only 1 concurrent request at a time
    };
  }

  // Circuit breaker for specific endpoints
  getCircuitBreaker(endpoint) {
    if (!this.circuitBreakers.has(endpoint)) {
      this.circuitBreakers.set(endpoint, {
        isOpen: false,
        failureCount: 0,
        lastFailure: 0,
        timeout: 0
      });
    }
    return this.circuitBreakers.get(endpoint);
  }

  // Check if circuit breaker is open
  isCircuitBreakerOpen(endpoint) {
    const breaker = this.getCircuitBreaker(endpoint);
    const now = Date.now();
    
    if (breaker.isOpen && now < breaker.timeout) {
      return true;
    }
    
    // Reset circuit breaker if timeout has passed
    if (breaker.isOpen && now >= breaker.timeout) {
      breaker.isOpen = false;
      breaker.failureCount = 0;
      console.log(`Circuit breaker reset for ${endpoint}`);
    }
    
    return false;
  }

  // Record failure for circuit breaker
  recordFailure(endpoint) {
    const breaker = this.getCircuitBreaker(endpoint);
    breaker.failureCount++;
    breaker.lastFailure = Date.now();
    
    // Open circuit breaker after 3 failures
    if (breaker.failureCount >= 3) {
      breaker.isOpen = true;
      breaker.timeout = Date.now() + 60000; // 1 minute timeout
      console.warn(`Circuit breaker opened for ${endpoint} after ${breaker.failureCount} failures`);
    }
  }

  // Record success for circuit breaker
  recordSuccess(endpoint) {
    const breaker = this.getCircuitBreaker(endpoint);
    breaker.failureCount = 0;
    breaker.isOpen = false;
  }

  // Check global throttling
  isThrottled() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.globalThrottle.lastRequest;
    
    if (timeSinceLastRequest < this.globalThrottle.minInterval) {
      return true;
    }
    
    if (this.activeRequests.size >= this.globalThrottle.maxConcurrent) {
      return true;
    }
    
    return false;
  }

  // Add request to active requests
  addRequest(requestId, endpoint) {
    this.activeRequests.set(requestId, {
      endpoint,
      startTime: Date.now()
    });
    this.globalThrottle.lastRequest = Date.now();
  }

  // Remove request from active requests
  removeRequest(requestId) {
    this.activeRequests.delete(requestId);
  }

  // Get request statistics
  getStats() {
    return {
      activeRequests: this.activeRequests.size,
      maxConcurrent: this.globalThrottle.maxConcurrent,
      circuitBreakers: Array.from(this.circuitBreakers.entries()).map(([endpoint, breaker]) => ({
        endpoint,
        isOpen: breaker.isOpen,
        failureCount: breaker.failureCount
      }))
    };
  }

  // Clear all circuit breakers (for testing or manual reset)
  resetCircuitBreakers() {
    this.circuitBreakers.clear();
    console.log('All circuit breakers reset');
  }
}

// Create singleton instance
const requestManager = new RequestManager();

// Export for use in services
export default requestManager;

// Helper function to wrap API calls with request management
export const withRequestManagement = (apiCall, endpoint) => {
  return async (...args) => {
    const requestId = `${endpoint}-${Date.now()}-${Math.random()}`;
    
    // Check circuit breaker
    if (requestManager.isCircuitBreakerOpen(endpoint)) {
      throw new Error(`Circuit breaker is open for ${endpoint}`);
    }
    
    // Check global throttling
    if (requestManager.isThrottled()) {
      throw new Error('Request throttled - too many concurrent requests');
    }
    
    // Add to active requests
    requestManager.addRequest(requestId, endpoint);
    
    try {
      const result = await apiCall(...args);
      requestManager.recordSuccess(endpoint);
      return result;
    } catch (error) {
      requestManager.recordFailure(endpoint);
      throw error;
    } finally {
      requestManager.removeRequest(requestId);
    }
  };
};
